import { getProjects, type Project } from "./projects";
import type { ProjectTypeId } from "./projectFilters";
import { getTypeLabel } from "./projectFilters";
import {
  PROJECT_SEGMENTS,
  getSegmentLabel,
  normalizeSegment,
  type ProjectSegmentId,
} from "./projectSegments";

/** Canonical URL segments under /ux-portfolio/ (matches original Jekyll permalinks) */
export const PORTFOLIO_CATEGORY_SLUGS = [
  "product-design",
  "mobile-design",
  "web-design",
  "motion-design",
  "branding-and-identity",
] as const;

export type PortfolioCategorySlug = (typeof PORTFOLIO_CATEGORY_SLUGS)[number];

const SLUG_TO_TYPE: Record<PortfolioCategorySlug, ProjectTypeId> = {
  "product-design": "product-design",
  "mobile-design": "mobile-design",
  "web-design": "web-design",
  "motion-design": "motion-design",
  "branding-and-identity": "identity",
};

/** Legacy short paths from old /portfolio/ URLs */
const LEGACY_SLUG_ALIASES: Record<string, PortfolioCategorySlug> = {
  productdesign: "product-design",
  "product-design": "product-design",
  mobile: "mobile-design",
  "mobile-design": "mobile-design",
  webdesign: "web-design",
  "web-design": "web-design",
  motion: "motion-design",
  "motion-design": "motion-design",
  identity: "branding-and-identity",
  "branding-and-identity": "branding-and-identity",
};

const TYPE_TO_SLUG: Record<ProjectTypeId, PortfolioCategorySlug> = {
  "product-design": "product-design",
  "mobile-design": "mobile-design",
  "web-design": "web-design",
  "motion-design": "motion-design",
  identity: "branding-and-identity",
};

export function normalizePortfolioSlug(raw: string): PortfolioCategorySlug | null {
  const key = raw.toLowerCase().replace(/\/$/, "");
  return LEGACY_SLUG_ALIASES[key] ?? null;
}

export function portfolioSlugToType(slug: PortfolioCategorySlug): ProjectTypeId {
  return SLUG_TO_TYPE[slug];
}

export function typeToPortfolioSlug(type: ProjectTypeId): PortfolioCategorySlug {
  return TYPE_TO_SLUG[type];
}

export function getPortfolioCategoryTitle(slug: PortfolioCategorySlug): string {
  return getTypeLabel(portfolioSlugToType(slug));
}

export function getPortfolioBasePath(): string {
  return "/ux-portfolio";
}

export function getPortfolioCategoryPath(slug: PortfolioCategorySlug): string {
  return `/ux-portfolio/${slug}`;
}

export type PortfolioSegmentSlug = ProjectSegmentId;

export function normalizePortfolioSegmentSlug(raw: string): PortfolioSegmentSlug | null {
  return normalizeSegment(raw.replace(/\/$/, ""));
}

export function getPortfolioSegmentPath(slug: PortfolioSegmentSlug): string {
  return `/ux-portfolio/${slug}`;
}

export function getPortfolioSegmentTitle(slug: PortfolioSegmentSlug): string {
  return getSegmentLabel(slug);
}

/** Resolve /ux-portfolio/:slug for list pages (design category or industry segment). */
export function resolvePortfolioListSlug(raw: string): {
  kind: "category" | "segment";
  slug: PortfolioCategorySlug | PortfolioSegmentSlug;
} | null {
  const category = normalizePortfolioSlug(raw);
  if (category) return { kind: "category", slug: category };
  const segment = normalizePortfolioSegmentSlug(raw);
  if (segment) return { kind: "segment", slug: segment };
  return null;
}

export function getAllPortfolioSegmentSlugs(): PortfolioSegmentSlug[] {
  return PROJECT_SEGMENTS.map((s) => s.id);
}

/** Build canonical project URL from markdown permalink or project metadata */
export function getProjectUrl(project: Project): string {
  if (project.permalink) {
    const path = project.permalink.replace(/^\/+|\/+$/g, "");
    return `/${path}`;
  }

  const type = project.types[0] ?? "product-design";
  const category = typeToPortfolioSlug(type);
  return `/ux-portfolio/${category}/${project.id}`;
}

/** Last segment of ux-portfolio/.../slug permalink */
export function getProjectSlugFromPermalink(permalink: string): string {
  const parts = permalink.replace(/^\/+|\/+$/g, "").split("/");
  return parts[parts.length - 1] ?? "";
}

/** Category segment from ux-portfolio/:category/:slug paths (supports legacy short slugs). */
export function getCategoryFromProjectPath(path: string): PortfolioCategorySlug | null {
  const parts = path.replace(/^\/+|\/+$/g, "").split("/");
  if (parts[0] !== "ux-portfolio" || !parts[1]) return null;
  return normalizePortfolioSlug(parts[1]);
}

function projectMatchesCategory(
  project: Project,
  normalizedCategory: PortfolioCategorySlug,
): boolean {
  if (project.permalink) {
    const permalinkCategory = getCategoryFromProjectPath(project.permalink);
    if (permalinkCategory) return permalinkCategory === normalizedCategory;
  }

  const primaryType = project.types[0] ?? "product-design";
  return typeToPortfolioSlug(primaryType) === normalizedCategory;
}

export function matchesProjectPath(
  project: Project,
  categorySlug: string,
  projectSlug: string,
): boolean {
  const normalizedCategory = normalizePortfolioSlug(categorySlug);
  if (!normalizedCategory) return false;

  const slugLower = decodeURIComponent(projectSlug).toLowerCase();

  if (project.id.toLowerCase() === slugLower) {
    return projectMatchesCategory(project, normalizedCategory);
  }

  if (project.permalink) {
    const permalinkSlug = getProjectSlugFromPermalink(project.permalink).toLowerCase();
    if (permalinkSlug !== slugLower) return false;
    return projectMatchesCategory(project, normalizedCategory);
  }

  return false;
}

export function getProjectByPortfolioPath(
  categorySlug: string,
  projectSlug: string,
): Project | undefined {
  return getProjects().find((p) => matchesProjectPath(p, categorySlug, projectSlug));
}
